import React, { useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv, formatDateTime } from "../master/Master";
import usePrivateLink from "../config/usePrivateLink";
import { useNavigate } from "react-router-dom";
import {
  distributionKitList,
  distributionKitAdd,
  distributionKitUpdate,
  distributionKitDelete,
} from "../../api/DistributionKit";
import AssignKitModal from "./AssignKitModal";
import KitAssignmentsModal from "./KitAssignmentsModal";
import DistributionKitLayout from "./DistributionKitLayout";
import Swal from "sweetalert2";

const GST_OPTIONS = [0, 5, 12, 18, 28];

const emptyForm = {
  id: null,
  name: "",
  description: "",
  quantity: "",
  last_date_to_purchase: "",
  is_free: false,
  price_without_gst: "",
  price_with_gst: "",
  gst: "",
  discount_amount: "",
};

export default function DistributionKitList() {
  usePrivateLink();
  const hasAccess = usePrivateLink();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [kits, setKits] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [assignKit, setAssignKit] = useState(null);
  const [viewAssignmentsKit, setViewAssignmentsKit] = useState(null);

  const getKitList = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (search) {
        addUrl += `&search=${encodeURIComponent(search)}`;
      }

      if (statusFilter !== "") {
        addUrl += `&status=${statusFilter}`;
      }

      const response = await distributionKitList(addUrl);

      if (response.data.success) {
        setKits(response?.data?.data?.data || response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getKitList();
  }, [search, statusFilter]);

  const stats = useMemo(() => {
    const total = kits.length;
    const active = kits.filter((k) => Number(k.status) === 1).length;
    const free = kits.filter((k) => k.is_free).length;

    return { total, active, free };
  }, [kits]);

  const openAddModal = () => {
    setIsEdit(false);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (kit) => {
    setIsEdit(true);

    const isFree = !!kit.is_free;
    const priceWithoutGst = isFree ? 0 : Number(kit.price_without_gst || 0);

    const gst = isFree ? 0 : Number(kit.gst || 0);

    const priceWithGst = isFree
      ? 0
      : (priceWithoutGst + (priceWithoutGst * gst) / 100).toFixed(2);

    setFormData({
      id: kit.id,
      name: kit.name || "",
      description: kit.description || "",
      quantity: kit.quantity ?? "",
      last_date_to_purchase: kit.last_date_to_purchase || "",
      is_free: isFree,
      price_without_gst: priceWithoutGst,
      price_with_gst: priceWithGst,
      gst: gst,
      discount_amount: isFree ? 0 : (kit.discount_amount ?? ""),
    });

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(emptyForm);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "is_free") {
        if (value === true) {
          next.gst = 0;
          next.price_without_gst = 0;
          next.price_with_gst = 0;
          next.discount_amount = 0;
        } else {
          next.gst = "";
          next.price_without_gst = "";
          next.price_with_gst = "";
          next.discount_amount = "";
        }
      }

      // Calculate Price With GST automatically
      if (field === "price_without_gst" || field === "gst") {
        const priceWithoutGst =
          field === "price_without_gst"
            ? Number(value || 0)
            : Number(prev.price_without_gst || 0);

        const gst =
          field === "gst" ? Number(value || 0) : Number(prev.gst || 0);

        next.price_with_gst = (
          priceWithoutGst +
          (priceWithoutGst * gst) / 100
        ).toFixed(2);
      }

      return next;
    });
  };

  const previewFinalPrice = formData.is_free
    ? 0
    : Math.max(
      0,
      Number(formData.price_with_gst || 0) -
      Number(formData.discount_amount || 0),
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        gst: formData.is_free ? 0 : formData.gst,
        discount_amount: formData.is_free
          ? 0
          : Number(formData.discount_amount || 0),
      };

      delete payload.id;

      const response = isEdit
        ? await distributionKitUpdate(formData.id, payload)
        : await distributionKitAdd(payload);

      if (response.data.success) {
        SweetAlertShow("success", "Successfully", response.data.message);
        closeModal();
        getKitList();
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (kit) => {
    const result = await Swal.fire({
      title: "Delete Kit?",
      text: `Are you sure you want to delete "${kit.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const response = await distributionKitDelete(kit.id);

      if (response.data.success) {
        SweetAlertShow(
          "success",
          "Successfully",
          response.data.message || "Kit deleted successfully.",
        );

        getKitList();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response.data.message || "Unable to delete kit.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const goToItems = (kit) => {
    navigate(`/distribution/kits/items?kit_id=${kit.id}`);
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              <DistributionKitLayout />
              <div className="row g-3">
                <div className="card border-0 shadow-sm mb-4 overflow-hidden">
                  <div className="card-body p-4">
                    <div className="row align-items-center g-4">
                      {/* Title */}
                      <div className="col-xl-5 col-lg-12">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                            style={{
                              width: 54,
                              height: 54,
                              minWidth: 54,
                            }}
                          >
                            <i className="ri-archive-stack-line fs-3 text-primary" />
                          </div>

                          <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <h4 className="mb-0 fw-bold">
                                Distribution Kits
                              </h4>

                              <span className="badge bg-label-primary rounded-pill">
                                {stats.total} Kits
                              </span>
                            </div>

                            <p className="text-muted mb-0 mt-1">
                              Manage kits, pricing and distribution items
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="col-xl-7 col-lg-12">
                        <div className="row g-3 justify-content-xl-end">
                          <div className="col-sm-4">
                            <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-primary h-100">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-2 bg-primary text-white"
                                style={{ width: 40, height: 40, minWidth: 40 }}
                              >
                                <i className="ri-archive-line fs-5" />
                              </div>

                              <div>
                                <div className="text-muted small">
                                  Total Kits
                                </div>
                                <div className="fs-5 fw-bold">
                                  {stats.total}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-sm-4">
                            <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-success h-100">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-2 bg-success text-white"
                                style={{ width: 40, height: 40, minWidth: 40 }}
                              >
                                <i className="ri-checkbox-circle-line fs-5" />
                              </div>

                              <div>
                                <div className="text-muted small">Active</div>
                                <div className="fs-5 fw-bold text-success">
                                  {stats.active}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-sm-4">
                            <div className="d-flex align-items-center gap-3 p-3 rounded-3 bg-label-info h-100">
                              <div
                                className="d-flex align-items-center justify-content-center rounded-2 bg-info text-white"
                                style={{ width: 40, height: 40, minWidth: 40 }}
                              >
                                <i className="ri-gift-line fs-5" />
                              </div>

                              <div>
                                <div className="text-muted small">
                                  Free Kits
                                </div>
                                <div className="fs-5 fw-bold text-info">
                                  {stats.free}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm">
                  <div className="card-header border-bottom px-3 px-md-4 py-3">
                    <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                      <div className="flex-grow-1">
                        <h5 className="mb-1 fw-bold">All Kits</h5>

                        <p className="text-muted small mb-0">
                          View and manage available distribution kits
                        </p>
                      </div>

                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ flexShrink: 0 }}
                      >
                        <select
                          className="form-select"
                          style={{ minWidth: 140, height: 42 }}
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">All Status</option>
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>

                        <div
                          className="input-group"
                          style={{ minWidth: 220, height: 42 }}
                        >
                          <span className="input-group-text bg-transparent">
                            <i className="ri-search-line" />
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search kits..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </div>
                        {hasAccess("/distribution/kit/add") && (

                          <button
                            type="button"
                            className="btn btn-primary text-nowrap"
                            onClick={openAddModal}
                          >
                            <i className="ri-add-line me-1" />
                            Add Kit
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table
                        className="table table-hover align-middle mb-0"
                        style={{ minWidth: 1100 }}
                      >
                        <thead>
                          <tr>
                            <th className="ps-4">Kit</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>GST</th>
                            <th>Final Price</th>
                            <th>Purchase Until</th>
                            <th>Status</th>
                            <th className="text-end pe-4">Actions</th>
                          </tr>
                        </thead>

                        <tbody>
                          {kits?.length === 0 && (
                            <tr>
                              <td colSpan={8}>
                                <div className="text-center py-5">
                                  <div
                                    className="d-flex align-items-center justify-content-center rounded-circle bg-label-primary mx-auto mb-3"
                                    style={{ width: 64, height: 64 }}
                                  >
                                    <i className="ri-archive-stack-line fs-2 text-primary" />
                                  </div>

                                  <h6 className="mb-1 fw-semibold">
                                    No kits found
                                  </h6>

                                  <p className="text-muted small mb-0">
                                    Try changing your search or filter, or add a
                                    new kit.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}

                          {kits?.map((kit) => (
                            <tr key={kit.id}>
                              <td className="ps-4">
                                <div className="d-flex align-items-center gap-3">
                                  <div
                                    className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                                    style={{
                                      width: 42,
                                      height: 42,
                                      minWidth: 42,
                                    }}
                                  >
                                    <i className="ri-archive-2-line fs-5 text-primary" />
                                  </div>

                                  <div style={{ minWidth: 0 }}>
                                    <div
                                      className="fw-semibold text-truncate"
                                      style={{ maxWidth: 260 }}
                                    >
                                      {kit.name}
                                    </div>

                                    <div className="d-flex align-items-center gap-2 mt-1">
                                      {kit.is_free ? (
                                        <span className="badge bg-label-success rounded-pill">
                                          <i className="ri-gift-line me-1" />
                                          Free
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <span className="fw-semibold">
                                  {kit.quantity}
                                </span>
                              </td>

                              <td>
                                {kit.is_free ? (
                                  <span className="text-success fw-semibold">
                                    Free
                                  </span>
                                ) : (
                                  <span className="fw-semibold">
                                    ₹{kit.price_with_gst}
                                  </span>
                                )}
                              </td>

                              <td>
                                <span className="badge bg-label-secondary">
                                  {kit.gst}%
                                </span>
                              </td>

                              <td>
                                <div className="fw-bold">
                                  {kit.is_free ? (
                                    <span className="text-success">₹0.00</span>
                                  ) : (
                                    <>₹{kit.final_price}</>
                                  )}
                                </div>

                                {!kit.is_free &&
                                  Number(kit.discount_amount) > 0 ? (
                                  <div className="text-success small mt-1">
                                    <i className="ri-price-tag-3-line me-1" />
                                    Discount ₹{kit.discount_amount}
                                  </div>
                                ) : null}
                              </td>

                              <td>
                                {kit.last_date_to_purchase ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="ri-calendar-line text-muted" />
                                    <span>
                                      {formatDateTime(
                                        kit.last_date_to_purchase,
                                      )}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>

                              <td>
                                {Number(kit.status) === 1 ? (
                                  <span className="badge bg-label-success rounded-pill px-3">
                                    <span
                                      className="d-inline-block rounded-circle bg-success me-1"
                                      style={{
                                        width: 6,
                                        height: 6,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                    Active
                                  </span>
                                ) : (
                                  <span className="badge bg-label-secondary rounded-pill px-3">
                                    <span
                                      className="d-inline-block rounded-circle bg-secondary me-1"
                                      style={{
                                        width: 6,
                                        height: 6,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                    Inactive
                                  </span>
                                )}
                              </td>

                              <td className="text-end pe-4">
                                <div className="d-inline-flex align-items-center gap-2">
                                  {hasAccess("/distribution/kit/assign") && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary rounded-pill px-3 d-inline-flex align-items-center"
                                      onClick={() => setAssignKit(kit)}
                                      title="Assign to Student/Batch"
                                    >
                                      <i className="ri-user-add-line me-1" />
                                      Assign
                                    </button>
                                  )}

                                  <div
                                    className="d-inline-flex align-items-center gap-1 border rounded-pill p-1"
                                    style={{ background: "var(--bs-body-bg)" }}
                                  >
                                    {hasAccess("/distribution/kit/assignments") && (
                                      <button
                                        type="button"
                                        className="btn btn-icon btn-sm rounded-circle btn-label-info d-flex align-items-center justify-content-center p-0"
                                        style={{ width: 30, height: 30 }}
                                        onClick={() => setViewAssignmentsKit(kit)}
                                        title="View Assignments"
                                      >
                                        <i className="ri-team-line" />
                                      </button>
                                    )}


                                    {hasAccess("/distribution/kit/items") && (
                                      <button
                                        type="button"
                                        className="btn btn-icon btn-sm rounded-circle btn-label-primary d-flex align-items-center justify-content-center p-0"
                                        style={{ width: 30, height: 30 }}
                                        onClick={() => goToItems(kit)}
                                        title="Manage Items"
                                      >
                                        <i className="ri-list-check-2" />
                                      </button>
                                    )}

                                    {hasAccess("/distribution/kit/edit") && (
                                      <button
                                        type="button"
                                        className="btn btn-icon btn-sm rounded-circle btn-label-warning d-flex align-items-center justify-content-center p-0"
                                        style={{ width: 30, height: 30 }}
                                        onClick={() => openEditModal(kit)}
                                        title="Edit"
                                      >
                                        <i className="ri-edit-line" />
                                      </button>
                                    )}
                                    {hasAccess("/distribution/kit/delete") && (

                                      <button
                                        type="button"
                                        className="btn btn-icon btn-sm rounded-circle btn-label-danger d-flex align-items-center justify-content-center p-0"
                                        style={{ width: 30, height: 30 }}
                                        onClick={() => handleDelete(kit)}
                                        title="Delete"
                                      >
                                        <i className="ri-delete-bin-line" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {kits?.length > 0 && (
                    <div className="card-footer border-top py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          Showing{" "}
                          <strong className="text-body">{kits.length}</strong>{" "}
                          kit
                          {kits.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header border-bottom px-4 py-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                        style={{ width: 44, height: 44 }}
                      >
                        <i className="ri-archive-stack-line fs-5 text-primary" />
                      </div>

                      <div>
                        <h5 className="modal-title fw-bold mb-0">
                          {isEdit
                            ? "Edit Distribution Kit"
                            : "Add Distribution Kit"}
                        </h5>

                        <small className="text-muted">
                          {isEdit
                            ? "Update kit information and pricing"
                            : "Create a new kit for distribution"}
                        </small>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeModal}
                    />
                  </div>

                  <div className="modal-body p-4">
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <i className="ri-information-line text-primary" />
                        <span className="fw-semibold">Basic Information</span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-8">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Kit Name"
                              value={formData.name}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              required
                            />
                            <label>
                              Name <sup className="text-danger">*</sup>
                            </label>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="number"
                              min={0}
                              className="form-control"
                              placeholder="Quantity"
                              value={formData.quantity}
                              onChange={(e) =>
                                handleChange("quantity", e.target.value)
                              }
                              required
                            />
                            <label>
                              Quantity <sup className="text-danger">*</sup>
                            </label>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="form-floating form-floating-outline">
                            <textarea
                              className="form-control"
                              style={{ minHeight: 90, resize: "vertical" }}
                              placeholder="Description"
                              value={formData.description}
                              onChange={(e) =>
                                handleChange("description", e.target.value)
                              }
                            />
                            <label>Description</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <i className="ri-settings-3-line text-primary" />
                        <span className="fw-semibold">
                          Distribution Settings
                        </span>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={formData.last_date_to_purchase}
                              onChange={(e) =>
                                handleChange(
                                  "last_date_to_purchase",
                                  e.target.value,
                                )
                              }
                            />
                            <label>Last Date To Purchase</label>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div
                            className={`h-100 d-flex align-items-center px-3 rounded-3 border ${formData.is_free ? "bg-label-success" : ""
                              }`}
                          >
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="is_free"
                                checked={formData.is_free}
                                onChange={(e) =>
                                  handleChange("is_free", e.target.checked)
                                }
                              />

                              <label
                                className="form-check-label fw-medium"
                                htmlFor="is_free"
                              >
                                <i
                                  className={`${formData.is_free
                                    ? "ri-gift-line text-success"
                                    : "ri-gift-line text-muted"
                                    } me-1`}
                                />
                                Free Kit
                              </label>
                            </div>
                          </div>
                        </div>

                        {!formData.is_free && (
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={formData.gst}
                                onChange={(e) =>
                                  handleChange("gst", e.target.value)
                                }
                                required
                              >
                                <option value="" disabled>
                                  Select GST
                                </option>

                                {GST_OPTIONS.map((g) => (
                                  <option key={g} value={g}>
                                    {g}%
                                  </option>
                                ))}
                              </select>

                              <label>
                                GST <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!formData.is_free && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <i className="ri-money-rupee-circle-line text-primary" />
                          <span className="fw-semibold">Pricing</span>
                        </div>

                        <div className="row g-3">
                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Price without GST"
                                value={formData.price_without_gst}
                                onChange={(e) =>
                                  handleChange(
                                    "price_without_gst",
                                    e.target.value,
                                  )
                                }
                                required
                              />
                              <label>
                                Price Without GST{" "}
                                <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Price with GST"
                                value={formData.price_with_gst}
                                readOnly
                                required
                              />
                              <label>
                                Price With GST{" "}
                                <sup className="text-danger">*</sup>
                              </label>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                className="form-control"
                                placeholder="Discount Amount"
                                value={formData.discount_amount}
                                onChange={(e) =>
                                  handleChange(
                                    "discount_amount",
                                    e.target.value,
                                  )
                                }
                              />
                              <label>Discount Amount</label>
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="rounded-3 border bg-label-primary p-3">
                              <div className="row align-items-center">
                                <div className="col">
                                  <div className="text-primary small fw-medium mb-1">
                                    FINAL PRICE
                                  </div>

                                  <div className="text-muted small">
                                    Price after applicable discount
                                  </div>
                                </div>

                                <div className="col-auto">
                                  <div className="fs-4 fw-bold text-primary">
                                    ₹{previewFinalPrice.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {formData.is_free && (
                      <div className="rounded-3 border border-success bg-label-success p-3">
                        <div className="d-flex align-items-start gap-3">
                          <i className="ri-gift-line fs-4 text-success" />

                          <div>
                            <div className="fw-semibold text-success">
                              Free Kit
                            </div>

                            <div className="text-muted small mt-1">
                              Pricing fields are disabled because this kit is
                              marked as free.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer border-top px-4 py-3">
                    <button
                      type="button"
                      className="btn btn-label-secondary"
                      onClick={closeModal}
                    >
                      <i className="ri-close-line me-1" />
                      Cancel
                    </button>

                    <button type="submit" className="btn btn-primary px-4">
                      <i className="ri-check-line me-1" />
                      {isEdit ? "Update Kit" : "Save Kit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}

      {assignKit && (
        <AssignKitModal
          kit={assignKit}
          onClose={() => setAssignKit(null)}
          onAssigned={getKitList}
        />
      )}

      {viewAssignmentsKit && (
        <KitAssignmentsModal
          kit={viewAssignmentsKit}
          onClose={() => setViewAssignmentsKit(null)}
        />
      )}
    </>
  );
}
